
import React from 'react';
import { Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ReviewCarousel } from './ReviewCarousel';
import { getCourseReviewCount } from '@/lib/rpcFunctions';

interface ReviewsSummaryProps {
  courseId: string;
  showFeaturedReviews?: boolean;
}

export function ReviewsSummary({ courseId, showFeaturedReviews = true }: ReviewsSummaryProps) {
  // Fetch average rating and count
  const { data: ratingData } = useQuery({
    queryKey: ['course-rating', courseId],
    queryFn: async () => {
      const { data: averageRating } = await supabase.rpc(
        'get_course_average_rating',
        { course_id: courseId }
      );
      
      const count = await getCourseReviewCount(courseId);
      
      return { 
        average: averageRating || 0, 
        count: count || 0 
      };
    }
  });

  // Fetch featured reviews
  const { data: featuredReviews } = useQuery({
    queryKey: ['featured-reviews', courseId],
    queryFn: async () => {
      const { data } = await supabase
        .from('course_reviews')
        .select(`
          id,
          rating,
          review,
          created_at,
          profiles (
            full_name,
            avatar_url
          )
        `)
        .eq('course_id', courseId)
        .eq('is_featured', true)
        .eq('is_public', true)
        .order('created_at', { ascending: false });
      
      return data || [];
    },
    enabled: showFeaturedReviews
  });

  if (!ratingData || ratingData.count === 0) {
    return null;
  }

  return (
    <section id="reviews" className="py-6 border-t">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Student Reviews</h2>
        
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-5 w-5 ${
                  star <= Math.round(ratingData.average)
                    ? "text-yellow-400 fill-yellow-400"
                    : "text-gray-300"
                }`}
              />
            ))}
          </div>
          <span className="text-lg font-semibold">
            {ratingData.average.toFixed(1)}
          </span>
          <span className="text-sm text-muted-foreground">
            ({ratingData.count} {ratingData.count === 1 ? 'review' : 'reviews'})
          </span>
        </div>
      </div>

      {showFeaturedReviews && featuredReviews && featuredReviews.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Featured Reviews</h3>
          <ReviewCarousel reviews={featuredReviews} />
        </div>
      )}
    </section>
  );
}
